import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../utils/api";
import TabbedFormLayout from "../../components/TabbedFormLayout/TabbedFormLayout";
import CustomInput from "../../components/Inputs/CustomInput";
import CustomSelect from "../../components/CustomSelect/CustomSelect";
import CustomCheckbox from "../../components/CustomCheckbox/CustomCheckbox";
import CustomCalendar from "../../components/CustomCalendar/CustomCalendar";
import styles from "./NewUserPage.module.scss";

interface Organization {
  id: string;
  name: string;
}

interface App {
  id: string;
  name: string;
}

export default function NewUserPage() {
  const navigate = useNavigate();
  const [organizationList, setOrganizationList] = useState<Organization[]>([]);
  const [appList, setAppList] = useState<App[]>([]);

  const [firstname, setFirstname] = useState("");
  const [middlename, setMiddlename] = useState("");
  const [lastname, setLastname] = useState("");
  const [email, setEmail] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [title, setTitle] = useState("");
  const [usertype, setUsertype] = useState("");
  const [issuperadmin, setIsSuperAdmin] = useState(false);

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [passwordlessEnabled, setPasswordlessEnabled] = useState(false);
  const [passwordlessMethod, setPasswordlessMethod] = useState("");
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [twoFactorType, setTwoFactorType] = useState("");
  const [recoveryEmailEnabled, setRecoveryEmailEnabled] = useState(false);
  const [recoveryEmail, setRecoveryEmail] = useState("");
  const [sessionInvalidated, setSessionInvalidated] = useState(false);
  const [accountLockedUntil, setAccountLockedUntil] = useState("");
  const [passwordLastChanged, setPasswordLastChanged] = useState("");
  const [securityPin, setSecurityPin] = useState("");

  const [languagePreference, setLanguagePreference] = useState("en");
  const [timezone, setTimezone] = useState("");
  const [companyAddress1, setCompanyAddress1] = useState("");
  const [companyAddress2, setCompanyAddress2] = useState("");
  const [companyUnit, setCompanyUnit] = useState("");
  const [companyState, setCompanyState] = useState("");
  const [companyPostal, setCompanyPostal] = useState("");
  const [personalAddress1, setPersonalAddress1] = useState("");
  const [personalAddress2, setPersonalAddress2] = useState("");
  const [personalUnit, setPersonalUnit] = useState("");
  const [personalState, setPersonalState] = useState("");
  const [personalPostal, setPersonalPostal] = useState("");
  const [shippingAddress1, setShippingAddress1] = useState("");
  const [shippingAddress2, setShippingAddress2] = useState("");
  const [shippingUnit, setShippingUnit] = useState("");
  const [shippingState, setShippingState] = useState("");
  const [shippingPostal, setShippingPostal] = useState("");

  const [apps, setApps] = useState<string[]>([]);
  const [activeOrganizationId, setActiveOrganizationId] = useState("");
  const [isOptenicsUser, setIsOptenicsUser] = useState(false);
  const [isPortalUser, setIsPortalUser] = useState(false);
  const [isPaidUser, setIsPaidUser] = useState(false);
  const [isAffiliatesUser, setIsAffiliatesUser] = useState(false);
  const [notificationPreferences, setNotificationPreferences] = useState("{}");
const [newOrganizationName, setNewOrganizationName] = useState("");
  useEffect(() => {
    fetchLists();
  }, []);

  const fetchLists = async () => {
    try {
      const [orgListRes, appListRes] = await Promise.all([
        api.get("/core/organization/list"),
        api.get("/core/app/list"),
      ]);
      setOrganizationList(orgListRes.data.organizations || []);
      setAppList(appListRes.data.apps || []);
    } catch (err) {
      console.error("Failed to fetch lists:", err);
    }
  };

  const handleSave = async () => {
    if (password !== confirmPassword) {
      alert("Passwords do not match.");
      return;
    }
console.log("Apps selected:", apps);
    try {
      const payload = {
        firstname,
        middlename,
        lastname,
        email,
        password, // Make sure backend expects this field!
        phone_number: phoneNumber,
        title,
        usertype,
        issuperadmin,
        passwordless_enabled: passwordlessEnabled,
        passwordless_method: passwordlessMethod || null,
        two_factor_enabled: twoFactorEnabled,
        two_factor_type: twoFactorType || null,
        recovery_email_enabled: recoveryEmailEnabled,
        recovery_email: recoveryEmail,
        session_invalidated: sessionInvalidated,
        account_locked_until: accountLockedUntil || null,
        password_last_changed: passwordLastChanged || null,
        security_pin: securityPin,
        language_preference: languagePreference || "en",
        timezone,
        company_address_1: companyAddress1,
        company_address_2: companyAddress2,
        company_unit: companyUnit,
        company_state: companyState,
        company_postal: companyPostal,
        personal_address_1: personalAddress1,
        personal_address_2: personalAddress2,
        personal_unit: personalUnit,
        personal_state: personalState,
        personal_postal: personalPostal,
        shipping_address_1: shippingAddress1,
        shipping_address_2: shippingAddress2,
        shipping_unit: shippingUnit,
        shipping_state: shippingState,
        shipping_postal: shippingPostal,
        apps,
        activeorganizationid: activeOrganizationId !== "new" ? activeOrganizationId : undefined,
        organization_name: activeOrganizationId === "new" ? newOrganizationName : undefined,
        is_optenics_user: isOptenicsUser,
        is_portal_user: isPortalUser,
        is_paid_user: isPaidUser,
        is_affiliates_user: isAffiliatesUser,
        notification_preferences: JSON.parse(notificationPreferences),
      };
      
      await api.post("/core/users/create", payload);
      navigate("/users");
    } catch (err) {
      console.error("Failed to create user:", err);
    }
  };

  const tabs = [
    {
      label: "General",
      content: (
        <div className={styles.formGrid}>
          <CustomInput label="First Name" value={firstname} onChange={setFirstname} />
          <CustomInput label="Middle Name" value={middlename} onChange={setMiddlename} />
          <CustomInput label="Last Name" value={lastname} onChange={setLastname} />
          <CustomInput label="Email" value={email} onChange={setEmail} type="email"/>
          <CustomInput label="Phone" value={phoneNumber} onChange={setPhoneNumber} type="tel"/>
          <CustomInput label="Title" value={title} onChange={setTitle} />
          <CustomSelect label="User Type" value={usertype} onChange={setUsertype}
            options={[
              { value: "admin", label: "Admin" },
              { value: "manager", label: "Manager" },
              { value: "user", label: "User" },
              { value: "superadmin", label: "Super Admin" },
            ]}
          />
          <CustomCheckbox label="Is Super Admin" checked={issuperadmin} onChange={setIsSuperAdmin} id="issuperadmin" />
        </div>
      ),
    },
    {
      label: "Auth Settings",
      content: (
        <div className={styles.formGrid}>
          <CustomInput label="Password" value={password} onChange={setPassword} type="password" />
          <CustomInput label="Confirm Password" value={confirmPassword} onChange={setConfirmPassword} type="password" />
          <CustomCheckbox label="Passwordless Enabled" checked={passwordlessEnabled} onChange={setPasswordlessEnabled} id="passwordlessEnabled" />
          <CustomSelect label="Passwordless Method" value={passwordlessMethod} onChange={setPasswordlessMethod}
            options={[
              { value: "magic_link", label: "Magic Link" },
              { value: "otp_email", label: "OTP Email" },
              { value: "otp_sms", label: "OTP SMS" },
              { value: "biometric", label: "Biometric" },
            ]}
          />
          <CustomCheckbox label="Two Factor Enabled" checked={twoFactorEnabled} onChange={setTwoFactorEnabled} id="twoFactorEnabled" />
          <CustomSelect label="Two Factor Type" value={twoFactorType} onChange={setTwoFactorType}
            options={[
              { value: "email", label: "Email" },
              { value: "sms", label: "SMS" },
            ]}
          />
          <CustomCheckbox label="Recovery Email Enabled" checked={recoveryEmailEnabled} onChange={setRecoveryEmailEnabled} id="recoveryEmailEnabled" />
          <CustomInput label="Recovery Email" value={recoveryEmail} onChange={setRecoveryEmail} />
          <CustomCheckbox label="Session Invalidated" checked={sessionInvalidated} onChange={setSessionInvalidated} id="sessionInvalidated" />
          <CustomCalendar label="Account Locked Until" value={accountLockedUntil} onChange={setAccountLockedUntil} />
          <CustomCalendar label="Password Last Changed" value={passwordLastChanged} onChange={setPasswordLastChanged} />
          <CustomInput label="Security Pin" value={securityPin} onChange={setSecurityPin} type="password"/>
        </div>
      ),
    },
    // User Settings and App Access tabs stay unchanged…
{
      label: "User Settings",
      content: (
        <div className={styles.formGrid}>
          <CustomSelect label="Language Preference" value={languagePreference} onChange={setLanguagePreference} type="language" />
          <CustomSelect label="Timezone" value={timezone} onChange={setTimezone} type="timezone" />
          <CustomInput label="Company Address 1" value={companyAddress1} onChange={setCompanyAddress1} />
          <CustomInput label="Company Address 2" value={companyAddress2} onChange={setCompanyAddress2} />
          <CustomInput label="Company Unit" value={companyUnit} onChange={setCompanyUnit} />
          <CustomSelect label="Company State" value={companyState} onChange={setCompanyState} type="state" />
          <CustomInput label="Company Postal" value={companyPostal} onChange={setCompanyPostal} />
          <CustomInput label="Personal Address 1" value={personalAddress1} onChange={setPersonalAddress1} />
          <CustomInput label="Personal Address 2" value={personalAddress2} onChange={setPersonalAddress2} />
          <CustomInput label="Personal Unit" value={personalUnit} onChange={setPersonalUnit} />
          <CustomSelect label="Personal State" value={personalState} onChange={setPersonalState} type="state" />
          <CustomInput label="Personal Postal" value={personalPostal} onChange={setPersonalPostal} />
          <CustomInput label="Shipping Address 1" value={shippingAddress1} onChange={setShippingAddress1} />
          <CustomInput label="Shipping Address 2" value={shippingAddress2} onChange={setShippingAddress2} />
          <CustomInput label="Shipping Unit" value={shippingUnit} onChange={setShippingUnit} />
          <CustomSelect label="Shipping State" value={shippingState} onChange={setShippingState} type="state" />
          <CustomInput label="Shipping Postal" value={shippingPostal} onChange={setShippingPostal} />
        </div>
      ),
    },
    {
      label: "App Access",
      content: (
        <div className={styles.formGrid}>
          <CustomSelect
  label="Active Organization"
  value={activeOrganizationId}
  onChange={setActiveOrganizationId}
  options={[
    ...organizationList.map(org => ({ value: org.id, label: org.name })),
    { value: "new", label: "Create New Organization" },
  ]}
/>

{activeOrganizationId === "new" && (
  <CustomInput
    label="New Organization Name"
    value={newOrganizationName}
    onChange={setNewOrganizationName}
/>
)}
          <CustomCheckbox label="Is Optenics User" checked={isOptenicsUser} onChange={setIsOptenicsUser} id="isOptenicsUser" />
          <CustomCheckbox label="Is Portal User" checked={isPortalUser} onChange={setIsPortalUser} id="isPortalUser" />
          <CustomCheckbox label="Is Paid User" checked={isPaidUser} onChange={setIsPaidUser} id="isPaidUser" />
          <CustomCheckbox label="Is Affiliates User" checked={isAffiliatesUser} onChange={setIsAffiliatesUser} id="isAffiliatesUser" />
          {appList.map(app => (
            <CustomCheckbox key={app.id} label={app.name} checked={apps.includes(app.id)} onChange={(checked) => {
              setApps(prev => checked ? [...prev, app.id] : prev.filter(a => a !== app.id));
            }} id={`app-${app.id}`} />
          ))}
          <CustomInput label="Notification Preferences (JSON)" value={notificationPreferences} onChange={setNotificationPreferences} />
        </div>
      ),
    },
  ];

  return <TabbedFormLayout tabs={tabs} onSave={handleSave} onCancel={() => navigate("/users")} />;
}
